import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "mainSlide",
    "thumbnail",
    "thumbnailImage",
    "thumbnailTrack",
    "thumbnailContainer",
    "prevButton",
    "nextButton",
    "label",
  ];

  static values = {
    thumbnailsPerView: Number,
    totalImages: Number,
    labels: Array,
    zoomable: { type: Boolean, default: false },
    pdfUrl: String,
    aspectRatio: String,
  };

  connect() {
    this.currentIndex = 0;
    this.thumbnailOffset = 0;
    this.updateButtonStates();

    this._resizeHandler = this.updateThumbnailPosition.bind(this);
    window.addEventListener("resize", this._resizeHandler);

    if (this.hasPdfUrlValue && this.pdfUrlValue) {
      this.loadPdf();
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._resizeHandler);
    this._pdfDoc?.destroy();
    this._pdfDoc = null;
  }

  // ─── Navigation

  next() {
    if (this.currentIndex < this.totalImagesValue - 1) {
      this.currentIndex++;
      this.updateDisplay();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateDisplay();
    }
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!isNaN(index) && index >= 0 && index < this.totalImagesValue) {
      this.currentIndex = index;
      this.updateDisplay();
    }
  }

  // ─── PDF loading

  async loadPdf() {
    this.showLoadingPlaceholder();

    try {
      const pdfjsLib = await this.pdfjsLib();
      const containerWidth = await this.measureContainerWidth();
      const doc = await pdfjsLib.getDocument({ url: this.pdfUrlValue }).promise;
      this._pdfDoc = doc;

      const labels = Array.from({ length: doc.numPages }, (_, i) => `Page ${i + 1}`);
      const imageUrls = new Array(doc.numPages);

      imageUrls[0] = await this.renderPageAsDataUrl(doc, 1, containerWidth);
      this.setImages(imageUrls.map((url) => url ?? imageUrls[0]), labels);

      for (let i = 2; i <= doc.numPages; i++) {
        imageUrls[i - 1] = await this.renderPageAsDataUrl(doc, i, containerWidth);
      }

      this.setImages(imageUrls, labels, { preserveIndex: true });
      doc.destroy();
      this._pdfDoc = null;
    } catch (error) {
      console.error("Failed to load PDF:", error);
      this.showErrorPlaceholder();
    }
  }

  async pdfjsLib() {
    const lib = await import("pdfjs-dist");
    if (!lib.GlobalWorkerOptions.workerSrc) {
      lib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();
    }
    return lib;
  }

  async renderPageAsDataUrl(doc, pageNumber, containerWidth) {
    const page = await doc.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = (containerWidth * 2) / baseViewport.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

    return canvas.toDataURL("image/png");
  }

  measureContainerWidth() {
    return new Promise((resolve) => {
      const read = () => {
        const width = this.slidesContainer?.getBoundingClientRect().width;
        if (width > 0) return resolve(width);
        requestAnimationFrame(read);
      };
      read();
    });
  }

  // ─── Display updates

  updateDisplay() {
    this.updateMainImage();
    this.updateThumbnailPosition();
    this.highlightActiveThumbnail();
    this.updateLabel();
    this.updateButtonStates();
  }

  updateMainImage() {
    this.mainSlideTargets.forEach((slide, index) => {
      slide.classList.toggle("hidden", index !== this.currentIndex);
    });
  }

  updateThumbnailPosition() {
    if (!this.hasThumbnailTrackTarget) return;

    const visibleStart = this.thumbnailOffset;
    const visibleEnd = this.thumbnailOffset + this.thumbnailsPerViewValue - 1;

    if (this.currentIndex < visibleStart) {
      this.thumbnailOffset = this.currentIndex;
    } else if (this.currentIndex > visibleEnd) {
      this.thumbnailOffset = this.currentIndex - this.thumbnailsPerViewValue + 1;
    }

    const maxOffset = Math.max(0, this.totalImagesValue - this.thumbnailsPerViewValue);
    this.thumbnailOffset = Math.min(Math.max(0, this.thumbnailOffset), maxOffset);

    const translateX = -(this.thumbnailOffset * (100 / this.thumbnailsPerViewValue));
    this.thumbnailTrackTarget.style.transform = `translateX(${translateX}%)`;
  }

  highlightActiveThumbnail() {
    this.thumbnailImageTargets.forEach((img, index) => {
      const active = index === this.currentIndex;
      img.classList.toggle("border-forest-500", active);
      img.classList.toggle("border-gray-200", !active);
      img.classList.toggle("opacity-60", !active);
    });
  }

  updateLabel() {
    if (!this.hasLabelTarget) return;
    const pageInfo = `Page ${this.currentIndex + 1}/${this.totalImagesValue}`;
    const label = this.hasLabelsValue ? this.labelsValue[this.currentIndex] : null;
    this.labelTarget.textContent = label ? `${pageInfo} - ${label}` : pageInfo;
  }

  updateButtonStates() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentIndex === 0;
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = this.currentIndex === this.totalImagesValue - 1;
    }
  }

  // ─── DOM rendering

  setImages(images, labels = [], { preserveIndex = false } = {}) {
    if (!images?.length) return;

    this.totalImagesValue = images.length;
    this.labelsValue = labels;

    if (!preserveIndex) {
      this.currentIndex = 0;
      this.thumbnailOffset = 0;
    }

    this.renderSlides(images, labels);
    this.renderThumbnails(images);
    this.updateNavVisibility(images.length);
    this.updateThumbnailPosition();
    this.updateLabel();
    this.updateButtonStates();
  }

  renderSlides(images, labels) {
    if (!this.slidesContainer) return;

    this.slidesContainer.innerHTML = images
      .map((url, index) => {
        const alt = (labels[index] ?? "").replace(/"/g, "&quot;");
        const loading = index === 0 ? "eager" : "lazy";
        const hidden = index !== 0;
        return this.zoomableValue
          ? this.zoomableSlideHTML(url, index, alt, loading, hidden)
          : this.slideHTML(url, index, alt, loading, hidden);
      })
      .join("");
  }

  renderThumbnails(images) {
    if (!this.hasThumbnailTrackTarget) return;

    const track = this.thumbnailTrackTarget;
    const itemWidth = 100.0 / this.thumbnailsPerViewValue;

    track.innerHTML = images
      .map((url, index) => this.thumbnailHTML(url, index, itemWidth))
      .join("");
  }

  updateNavVisibility(imageCount) {
    if (!this.hasPrevButtonTarget) return;

    this.prevButtonTarget.parentElement?.classList.toggle("hidden", imageCount <= 1);

    const hideSideButtons = imageCount < 5;
    this.prevButtonTarget.classList.toggle("sm:hidden", hideSideButtons);
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("sm:hidden", hideSideButtons);
    }
  }

  showLoadingPlaceholder() {
    if (!this.slidesContainer) return;
    this.slidesContainer.innerHTML = `
      <div class="flex items-center justify-center h-[60vh] bg-gray-100 rounded">
        <p class="text-sm text-gray-400">Loading PDF...</p>
      </div>
    `;
  }

  showErrorPlaceholder() {
    if (!this.slidesContainer) return;
    this.slidesContainer.innerHTML = `
      <div class="flex items-center justify-center p-8 text-gray-500">
        <p>Unable to load draft. Please try again or contact support.</p>
      </div>
    `;
  }

  get slidesContainer() {
    return this.mainSlideTargets[0]?.parentElement ?? this.element.querySelector(".relative.w-full");
  }

  // ─── HTML templates

  slideHTML(url, index, alt, loading, hidden) {
    return `
      <div class="${hidden ? "hidden" : ""}" data-gallery-carousel-target="mainSlide" data-index="${index}">
        <img src="${url}" alt="${alt}" class="w-full max-h-[60vh] object-contain ${this.aspectRatioValue ? `aspect-${this.aspectRatioValue}` : "min-h-[300px]"} rounded-xl" loading="${loading}">
      </div>`;
  }

  zoomableSlideHTML(url, index, alt, loading, hidden) {
    return `
      <div class="${hidden ? "hidden" : ""}" data-gallery-carousel-target="mainSlide" data-index="${index}">
        <div data-controller="image-zoom-pan" class="flex flex-col gap-2">
          <div data-image-zoom-pan-target="viewport" class="overflow-hidden w-full h-[60vh] flex justify-center items-center bg-gray-100 rounded cursor-default">
            <div data-image-zoom-pan-target="content" class="inline-block">
              <img src="${url}" alt="${alt}" class="max-h-[60vh] w-auto object-contain block" loading="${loading}">
            </div>
          </div>
          <div data-image-zoom-pan-target="toolbar" class="flex items-center gap-2 py-1">
            <button type="button" data-zoom-action="zoom-in" class="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700" title="Zoom in">+</button>
            <button type="button" data-zoom-action="zoom-out" class="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700" title="Zoom out">&minus;</button>
            <button type="button" data-zoom-action="zoom-reset" class="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700" title="Reset zoom">Reset</button>
          </div>
        </div>
      </div>`;
  }

  thumbnailHTML(url, index, itemWidth) {
    const active = index === this.currentIndex;
    const borderClass = active ? "border-forest-500" : "opacity-60 border-gray-200";
    return `
      <div class="flex-shrink-0 cursor-pointer" data-gallery-carousel-target="thumbnail" data-action="click->gallery-carousel#goToSlide" data-index="${index}" style="width: calc(${itemWidth}% - 0.25rem);">
        <img src="${url}" alt="Thumbnail ${index + 1}" class="w-full h-auto object-cover rounded border-2 border-solid transition-all duration-200 ${borderClass}" data-gallery-carousel-target="thumbnailImage" loading="lazy">
      </div>`;
  }
}
